import { useTranslations } from 'next-intl';

import type { AlertModalProps } from '@/types/modal';

import { Button } from '../ui/button';
import { Icon } from '../ui/icon';
import BaseModal from './BaseModal';

type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

type ExtendedAlertModalProps = AlertModalProps & {
  severity?: AlertSeverity;
};

const severityIcon: Record<AlertSeverity, string> = {
  info: 'lucide:info',
  success: 'lucide:circle-check',
  warning: 'lucide:triangle-alert',
  error: 'lucide:circle-alert',
};

// MD3 tonal-container chip surfaces for the status icon above the title.
const severityChipClass: Record<AlertSeverity, string> = {
  info: 'bg-info-container text-on-info-container',
  success: 'bg-success-container text-on-success-container',
  warning: 'bg-warning-container text-on-warning-container',
  error: 'bg-error-container text-on-error-container',
};

function AlertModal(props: ExtendedAlertModalProps) {
  const translate = useTranslations();

  const {
    title,
    continueText,
    open,
    onOpenChange,
    description,
    onCancel,
    onContinue,
    className,
    children,
    severity = 'info',
    isLoadingConfirmation = false,
  } = props;

  const finalContinueText = continueText ?? translate('modal.continue');
  const finalCancelText = translate('modal.cancel');

  const iconChip = (
    <div
      className={`flex h-10 w-10 items-center justify-center rounded-full ${severityChipClass[severity]}`}
    >
      <Icon icon={severityIcon[severity]} size={20} />
    </div>
  );

  const footer = (
    <>
      {onCancel !== undefined && (
        <Button
          onClick={() => {
            onCancel();
          }}
          disabled={isLoadingConfirmation === true}
          type="button"
          variant="text"
          size="default"
        >
          {finalCancelText}
        </Button>
      )}
      {onContinue !== undefined && (
        <Button
          onClick={() => {
            onContinue();
          }}
          loading={isLoadingConfirmation}
          type="button"
          variant={severity === 'error' ? 'error' : 'primary'}
          size="default"
        >
          {finalContinueText}
        </Button>
      )}
    </>
  );

  return (
    <BaseModal
      onOpenChange={onOpenChange}
      open={open}
      title={title}
      description={description}
      className={className}
      headerLeading={iconChip}
      footer={footer}
    >
      {children}
    </BaseModal>
  );
}

export default AlertModal;
